/**
 * The three views of the in-season markdown question — run, output, explanation.
 *
 * Planner review, 2026-09-25: "Price and markdown" was one long page holding the question
 * (which run, which options, which objective, which registry rows), the answer (the price
 * steps and their decisions, the path, the frontier) and the evidence under it (the fitted
 * elasticities). It is three routes, declared as the `children` of one navigation entry in
 * this chapter's manifest.
 *
 * **Moving between them is the rail's job, not the page's** (DEC-84, client rule 1, ADR 9023's
 * amendment of 2026-09-30): the rail draws the three as sub-entries of Price & markdown, and
 * carries the address bar's query to whichever the planner opens, so the output a planner
 * opens is still the output of the question they just posed (G2.2). The step strip that headed
 * each view, and the "to Output" / "to Explanation" / "Go to Run" buttons, are gone.
 *
 * What stays here is the one path a view still needs by name: Run's Solve lands on Output,
 * because that is where the answer to the action is.
 */

import { useEffect } from 'react';

import { EXPLAIN_PARAM, EXPLAIN_VIEW, EXPLAIN_VIEW_PARAM, MARKDOWN_VIEW_PARAM } from './scope';

/**
 * The views, in the order the question is asked, then what is done with the answer.
 *
 * Client rule 3 (2026-09-30, "one page, one function"; DEC-88): Run poses the question and
 * nothing else; the saved configurations of that question, the run's price decisions and the
 * stock position the solve opens from are each a view of their own. Registry rows are not a
 * view any more: they are the Pricing tab of the parameter register, in the rail.
 */
export const MARKDOWN_VIEW_PATHS = {
  run: '/inseason/markdown',
  output: '/inseason/markdown/output',
  // Issue 226 (giro 3 «Dopo» point 1): the margin-versus-cash frontier left Output for a page of
  // its own, and the run's recommendation (the calendar and its refusals) too — the calendar under
  // Pricing › Pending approvals in the rail (owner ruling 2026-10-07), not under Price and markdown.
  frontier: '/inseason/markdown/frontier',
  calendar: '/inseason/markdown/calendar',
  approvals: '/inseason/markdown/approvals',
  stock: '/inseason/markdown/stock',
  explanation: '/inseason/markdown/explanation',
  configurations: '/inseason/markdown/configurations',
} as const;

/**
 * Where an old Output panel now lives (issue 226): `?markdownView=` named one of the four panels
 * the one page held. The frontier and the run's price steps (`actions`, the calendar) are pages of
 * their own; the path and the batch are Result itself. The key is dropped on the way — the page
 * is the view now, and a link that kept it would be moved again.
 */
const LEGACY_VIEW_PATHS: Readonly<Record<string, string>> = {
  frontier: MARKDOWN_VIEW_PATHS.frontier,
  actions: MARKDOWN_VIEW_PATHS.calendar,
  path: MARKDOWN_VIEW_PATHS.output,
  batch: MARKDOWN_VIEW_PATHS.output,
};

/**
 * Where an old Output link now lives, or `null` when it is already where it belongs.
 *
 * `?view=explain` (the Cockpit's "Explain") and `?explain=<price_action_id>` (one step) opened
 * the why of a price DECISION, and the decisions are the Pending approvals view now. The query
 * travels unchanged, so the run and the step still name themselves there. `?markdownView=` named
 * a panel of the one-page Output (above), and is dropped. Pure, so the test asserts on strings;
 * `useLegacyOutputRedirect` replaces the history entry with it.
 */
export function legacyOutputRedirect(pathname: string, search: string): string | null {
  if (pathname.replace(/\/+$/, '') !== MARKDOWN_VIEW_PATHS.output) return null;
  const params = new URLSearchParams(search);
  const opensWhy = params.get(EXPLAIN_VIEW_PARAM) === EXPLAIN_VIEW || (params.get(EXPLAIN_PARAM) ?? '').trim() !== '';
  if (opensWhy) {
    const query = params.toString();
    return query === '' ? MARKDOWN_VIEW_PATHS.approvals : `${MARKDOWN_VIEW_PATHS.approvals}?${query}`;
  }
  if (!params.has(MARKDOWN_VIEW_PARAM)) return null;
  // An unknown value names no panel: it is dropped and the link stays on Result.
  const target = LEGACY_VIEW_PATHS[params.get(MARKDOWN_VIEW_PARAM)?.trim() ?? ''] ?? MARKDOWN_VIEW_PATHS.output;
  params.delete(MARKDOWN_VIEW_PARAM);
  const query = params.toString();
  return query === '' ? target : `${target}?${query}`;
}

/** Moves an old Output link to where its panel lives now, once, on mount — REPLACED, not pushed. */
export function useLegacyOutputRedirect(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = legacyOutputRedirect(window.location.pathname, window.location.search);
    if (target === null) return;
    window.history.replaceState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
}
